/**
 * Utility functions for working with SVG content
 */

export const SVG_NAMESPACE = 'http://www.w3.org/2000/svg'
export const XHTML_NAMESPACE = 'http://www.w3.org/1999/xhtml'

interface SVGDimensions {
  width: number
  height: number
}

/**
 * Create an SVG element with the correct namespace
 *
 * @param tagName - The tag name of the element to create
 * @param document - The document to create the element in
 * @returns A new SVG element
 */
export function createSvgElement(tagName: string, document: Document): Element {
  return document.createElementNS(SVG_NAMESPACE, tagName)
}

/**
 * Create an XHTML element with the correct namespace (for use in foreignObject)
 *
 * @param tagName - The tag name of the element to create
 * @param document - The document to create the element in
 * @returns A new XHTML element
 */
export function createXhtmlElement(tagName: string, document: Document): Element {
  return document.createElementNS(XHTML_NAMESPACE, tagName)
}

/**
 * Ensure the SVG has a defs section, creating one if it doesn't exist
 *
 * @param svgDoc - The SVG document
 * @returns The defs element
 */
export function ensureDefs(svgDoc: Document): Element {
  let defs = svgDoc.querySelector('defs')
  if (!defs) {
    defs = createSvgElement('defs', svgDoc)
    svgDoc.documentElement.insertBefore(defs, svgDoc.documentElement.firstChild)
  }
  return defs
}

/**
 * Create an image pattern element for use in an SVG
 *
 * @param svgDoc - The SVG document
 * @param imageUrl - The URL of the image to use in the pattern
 * @param patternPrefix - Optional prefix for the pattern ID (defaults to 'pattern')
 * @returns An object containing the pattern element and its ID
 */
export function createImagePattern(
  svgDoc: Document,
  imageUrl: string,
  patternPrefix = 'pattern',
): { pattern: Element; patternId: string } {
  // Generate a unique ID for our pattern
  const patternId = `${patternPrefix}-${Math.random().toString(36).substring(2, 15)}`

  // Create pattern element with objectBoundingBox units for scaling to path bounds
  const pattern = createSvgElement('pattern', svgDoc)
  pattern.setAttribute('id', patternId)
  pattern.setAttribute('patternContentUnits', 'objectBoundingBox')
  pattern.setAttribute('width', '1')
  pattern.setAttribute('height', '1')

  // Create image element inside pattern
  const image = createSvgElement('image', svgDoc)
  image.setAttribute('href', imageUrl)
  image.setAttribute('x', '0')
  image.setAttribute('y', '0')
  image.setAttribute('width', '1')
  image.setAttribute('height', '1')
  image.setAttribute('preserveAspectRatio', 'xMidYMid slice')

  pattern.appendChild(image)

  return { pattern, patternId }
}

/**
 * Create a foreignObject element with the specified dimensions
 *
 * @param svgDoc - The SVG document
 * @param x - The x coordinate
 * @param y - The y coordinate
 * @param width - The width
 * @param height - The height
 * @param transform - Optional transform attribute
 * @returns A new foreignObject element
 */
export function createForeignObject(
  svgDoc: Document,
  x: string | number,
  y: string | number,
  width: string | number,
  height: string | number,
  transform?: string,
): SVGForeignObjectElement {
  const foreignObject = createSvgElement('foreignObject', svgDoc) as SVGForeignObjectElement

  foreignObject.setAttribute('x', x.toString())
  foreignObject.setAttribute('y', y.toString())
  foreignObject.setAttribute('width', width.toString())
  foreignObject.setAttribute('height', height.toString())

  if (transform) {
    foreignObject.setAttribute('transform', transform)
  }

  return foreignObject
}

/**
 * Extract width and height dimensions from an SVG template string
 *
 * @param svgTemplate - The SVG template string to parse
 * @param defaultWidth - Default width to use if parsing fails (defaults to 600)
 * @param defaultHeight - Default height to use if parsing fails (defaults to 600)
 * @returns An object with width and height properties
 */
export function extractSVGDimensions(
  svgTemplate: string,
  defaultWidth = 600,
  defaultHeight = 600,
): SVGDimensions {
  try {
    // Parse dimensions from SVG
    const svgDoc = new DOMParser().parseFromString(svgTemplate, 'image/svg+xml')
    const svgElement = svgDoc.documentElement

    // Check for parsing errors
    const parseError = svgDoc.querySelector('parsererror')
    if (parseError) {
      return {
        width: defaultWidth,
        height: defaultHeight,
      }
    }

    // Check if the document is an SVG
    if (svgElement.tagName.toLowerCase() !== 'svg') {
      return {
        width: defaultWidth,
        height: defaultHeight,
      }
    }

    // Get width and height attributes, or use viewBox if available
    let width = svgElement.getAttribute('width')
    let height = svgElement.getAttribute('height')

    // If width/height are not specified, try to get from viewBox
    if ((!width || !height) && svgElement.getAttribute('viewBox')) {
      const viewBox = svgElement.getAttribute('viewBox').split(' ')
      if (viewBox.length === 4) {
        width = viewBox[2]
        height = viewBox[3]
      }
    }

    return {
      width: parseInt(width || defaultWidth.toString()),
      height: parseInt(height || defaultHeight.toString()),
    }
  } catch (error) {
    // Default fallback dimensions
    return {
      width: defaultWidth,
      height: defaultHeight,
    }
  }
}
